import {
  chakra,
  forwardRef,
  HTMLChakraProps,
  omitThemingProps,
  ThemingProps,
  useFormControlContext,
  useStyleConfig,
  useStyles,
} from '@chakra-ui/react';
import classnames from 'classnames';

export interface FormLabelProps
  extends HTMLChakraProps<'label'>,
    ThemingProps<'FormLabel'> {
  requiredIndicator?: React.ReactElement;
  optionalIndicator?: React.ReactNode;
}

export const FormLabel = forwardRef<FormLabelProps, 'label'>(
  (passedProps, ref) => {
    const styles = useStyleConfig('FormLabel', passedProps);
    const props = omitThemingProps(passedProps);

    const {
      className: _className,
      children,
      requiredIndicator = <RequiredIndicator mr={1} />,
      optionalIndicator = null,
      ...rest
    } = props;

    const field = useFormControlContext();
    const ownProps = field?.getLabelProps(rest, ref) ?? { ref, ...rest };

    return (
      <chakra.label
        {...ownProps}
        className={classnames('chakra-form__label', props.className)}
        __css={{
          display: 'block',
          textAlign: 'start',
          ...styles,
        }}
      >
        {field?.isRequired ? requiredIndicator : optionalIndicator}
        {children}
      </chakra.label>
    );
  },
);

export type RequiredIndicatorProps = HTMLChakraProps<'span'>;

/**
 * Used to show a "required" text or an asterisks (*) to indicate that
 * a field is required.
 */
export const RequiredIndicator = forwardRef<RequiredIndicatorProps, 'span'>(
  (props, ref) => {
    const field = useFormControlContext();
    const styles = useStyles();

    if (!field?.isRequired) return null;

    const className = classnames(
      'chakra-form__required-indicator',
      props.className,
    );

    return (
      <chakra.span
        {...field?.getRequiredIndicatorProps(props, ref)}
        __css={styles.requiredIndicator}
        className={className}
      />
    );
  },
);
